package horcrux.frontend.common.views.chat

import horcrux.common.api.APILimits
import horcrux.common.api.dtos.types.ChatModelConfigs
import horcrux.common.api.dtos.types.ChatModelID
import horcrux.common.api.dtos.types.NumChunks
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.State
import horcrux.frontend.common.extensions.marginVertical
import horcrux.frontend.common.views.components.containers.DialogAction
import horcrux.frontend.common.views.components.containers.DialogView
import horcrux.frontend.common.views.components.containers.VStack
import horcrux.frontend.common.views.components.input.ContainedButton
import horcrux.frontend.common.views.components.input.selectors.DropdownSelectorProps
import horcrux.frontend.common.views.components.input.selectors.dropdownSelector
import horcrux.frontend.common.views.components.input.sliderWithHelp
import mui.material.DialogContent
import mui.material.DialogTitle
import mui.system.sx
import react.FC
import react.Props
import web.cssom.pt

data class ChatModelConfigsState(
    val maxNumChunksState: State<NumChunks>,
    val chatModelIDState: State<ChatModelID>,
) {
    constructor() : this(State(DEFAULT_MAX_NUM_CHUNKS), State(DEFAULT_CHAT_MODEL_ID))

    val chatModelConfigs: ChatModelConfigs
        get() = ChatModelConfigs(maxNumChunksState.get(), chatModelIDState.get())

    fun reset() {
        maxNumChunksState.reset()
        chatModelIDState.reset()
    }

    companion object {
        private val DEFAULT_MAX_NUM_CHUNKS: NumChunks = NumChunks(5)
        private val DEFAULT_CHAT_MODEL_ID: ChatModelID = ChatModelID.GPT_4O_MINI
    }
}

external interface EditChatModelConfigsDialogProps : Props {
    var showDialogState: BooleanState
    var chatModelConfigsState: ChatModelConfigsState
}

val ChatModelIDSelector: FC<DropdownSelectorProps<ChatModelID>> = ChatModelID.dropdownSelector()
val NumChunksSliderWithHelp =
    sliderWithHelp({ number -> NumChunks(number.toInt()) }) { value -> value.value }

val EditChatModelConfigsDialog = FC<EditChatModelConfigsDialogProps> { props ->
    val title = "Edit Chat Model Configs"
    DialogView {
        this.title = title
        this.showDialogState = props.showDialogState
        this.dialogActionsMaybe = listOf(
            DialogAction("Reset") { _, _ -> props.chatModelConfigsState.reset() },
            DialogAction("OK") { _, _ -> props.showDialogState.setFalse()}
        )
        DialogTitle { +title }
        DialogContent {
            VStack {
                ChatModelIDSelector {
                    this.id = "chat-model-selector"
                    this.labelMaybe = "Chat Model"
                    this.helpTextMaybe =
                        """This is the Large Language Model that will be used to answer your questions. This is a useful setting because some models are more powerful than others, and some can use more information to answer your question. Current options are
                   ${ChatModelID.getFormattedDocumentationString()}""".trimIndent()
                    this.currentValueState = props.chatModelConfigsState.chatModelIDState
                    this.sx {
                        this.marginVertical = 10.pt
                    }
                }

                NumChunksSliderWithHelp {
                    this.id = "edit-max-relevant-documents"
                    this.label = "Max Relevant Documents"
                    this.min = APILimits.MIN_NUM_CHUNKS
                    this.max = APILimits.MAX_NUM_CHUNKS
                    this.step = 1
                    this.valueState = props.chatModelConfigsState.maxNumChunksState
                    this.helpText =
                        """When you ask a question we will search your collection for relevant portions of your documents and include them as context to help answer your question. This is the maximum such number. This is a useful setting because we can only use so much information to help answer your question."""
                }
            }
        }
    }
}
